import { Model } from "./model";
import type { CostItem, DatedFinanceInput } from "./dated-finance-types";
import type { OwnershipInput } from "./dated-ownership";
import type { Scenario } from "./types";

export type DatedMigrationPreview = {
  sourceScenarioId: string;
  anchorDate: string;
  sourcedCostAmounts: Array<{
    category: string;
    amount: number;
    source: string;
  }>;
  assumedDates: string[];
  unresolved: string[];
};

const finiteCost = (category: string, amount: number, source: string) =>
  Number.isFinite(amount) && amount > 0 ? { category, amount, source } : null;

/**
 * Extracts only the legacy project's pre-finance cost stack. The dated engine
 * deliberately does not inherit legacy construction interest, line fees, debt
 * capacity or equity carry: those require a dated facility and payment schedule.
 */
export function sourcePreFinanceCosts(
  source: Scenario,
): DatedMigrationPreview["sourcedCostAmounts"] {
  const developer = Model.compute(source).developer;
  return [
    finiteCost("land", developer.purchasePrice, "legacy model: purchase price"),
    finiteCost("stamp-duty", developer.stampDuty, "legacy model: stamp duty"),
    finiteCost(
      "acquisition-fee",
      developer.acquisitionFee,
      "legacy model: acquisition fee",
    ),
    finiteCost(
      "hard-construction",
      developer.hardConstruction,
      "legacy model: hard construction",
    ),
    finiteCost(
      "site-works",
      developer.siteWorksConstruction,
      "legacy model: site works",
    ),
    finiteCost(
      "preliminaries",
      developer.preliminaries,
      "legacy model: preliminaries",
    ),
    finiteCost(
      "builder-margin",
      developer.builderMargin,
      "legacy model: builder margin",
    ),
    finiteCost(
      "consultants",
      developer.consultantCosts,
      "legacy model: consultant costs",
    ),
    finiteCost(
      "statutory",
      developer.statutoryCosts,
      "legacy model: statutory costs",
    ),
    finiteCost(
      "planning-approval",
      developer.planningApprovalCosts,
      "legacy model: planning approval costs",
    ),
    finiteCost(
      "developer-approval-share",
      developer.developerApprovalShare,
      "legacy model: developer approval share",
    ),
    finiteCost(
      "contingency",
      developer.constructionContingency,
      "legacy model: contingency",
    ),
    finiteCost(
      "development-management",
      developer.developmentManagementFee,
      "legacy model: development management fee",
    ),
    finiteCost(
      "due-diligence",
      developer.otherDdCosts,
      "legacy model: other due diligence",
    ),
    finiteCost(
      "tenant-relocation",
      developer.tenantRelocationCosts,
      "legacy model: tenant relocation",
    ),
    finiteCost(
      "amenity-construction",
      developer.amenityConstruction,
      "legacy model: amenity construction",
    ),
  ].filter((item): item is NonNullable<typeof item> => item !== null);
}

/** Builds a reviewable migration preview without mutating or re-identifying the source. */
export function previewDatedMigration(
  source: Scenario,
  anchorDate: string,
): DatedMigrationPreview {
  const sourcedCostAmounts = sourcePreFinanceCosts(source);
  return {
    sourceScenarioId: source.id,
    anchorDate,
    sourcedCostAmounts,
    assumedDates: sourcedCostAmounts.map(
      (item) =>
        `${item.category}: ${anchorDate} (initial assumed payment date)`,
    ),
    unresolved: [
      "Every migrated cost payment date is an initial assumption and requires review.",
      "Programme milestones and dependencies require review.",
      "Facilities, funding terms, equity budget and refinance require review.",
      "Ownership lifecycle costs, terminal value and selling costs require review.",
    ],
  };
}

/** Produces a standalone dated draft; the source scenario is never mutated. */
export function datedInputsFromLegacy(
  source: Scenario,
  anchorDate: string,
): {
  preview: DatedMigrationPreview;
  finance: DatedFinanceInput;
  ownership: OwnershipInput;
} {
  const preview = previewDatedMigration(source, anchorDate);
  const end = new Date(`${anchorDate}T00:00:00Z`);
  end.setUTCFullYear(
    end.getUTCFullYear() + Math.max(1, source.assumptions.modelYears),
  );
  const endDate = end.toISOString().slice(0, 10);
  const costs: CostItem[] = preview.sourcedCostAmounts.map((item, index) => ({
    id: `legacy-${index}-${item.category}`,
    category: item.category,
    amount: item.amount,
    date: anchorDate,
  }));
  const legacy = Model.compute(source);
  const yearlyDate = (year: number) => {
    const date = new Date(`${anchorDate}T00:00:00Z`);
    date.setUTCFullYear(date.getUTCFullYear() + Math.max(0, year - 1));
    return date.toISOString().slice(0, 10);
  };
  // These are retained as dated, reviewable schedule assumptions. They are not
  // legacy returns and their anchor is deliberately disclosed in the preview.
  const landlordRent = legacy.years.map((year) => ({
    date: yearlyDate(year.year),
    amount: year.rentGross + year.amenityRent,
    category: `legacy Y${year.year} operator rent (assumed date)`,
  }));
  const operatorRevenue = legacy.years.map((year) => ({
    date: yearlyDate(year.year),
    amount: year.totalRevenue,
    category: `legacy Y${year.year} operator revenue (assumed date)`,
  }));
  const operatorExpenses = legacy.years.map((year) => ({
    date: yearlyDate(year.year),
    amount: year.variableCosts,
    category: `legacy Y${year.year} variable costs only (assumed date)`,
  }));
  preview.unresolved.push(
    "Legacy annual rent and operator profiles were shifted to the anchor date as reviewable schedule assumptions; fixed operating costs require dated confirmation.",
  );
  if (
    source.assumptions.dmFeeIncludeHoldingInterest ||
    source.assumptions.dmFeeIncludeLineFee ||
    source.assumptions.dmFeeIncludeConstructionInterest
  ) {
    preview.unresolved.push(
      "Legacy development-management fee used a basis that may include holding or finance; it is migrated as a fixed source amount pending basis review.",
    );
  }
  return {
    preview,
    finance: {
      startDate: anchorDate,
      endDate,
      costs,
      landlordRent,
      operatorRevenue,
      operatorExpenses,
      unresolvedInputs: preview.unresolved,
    },
    ownership: {
      valuationDate: anchorDate,
      endDate,
      annualDiscountRate: source.assumptions.schoolWacc,
      leasePayments: [],
      ownerAcquisitionAndBuildCosts: [],
      terminalValue: null,
      sellingCosts: null,
    },
  };
}
